import { Box } from "@jewlr/storybook/core"
import { Link } from "react-router-dom"

import { Section, Text } from "@jewlr/storybook/info"
import { jewlr_t } from "helpers/application"

const SMSPrivacyPolicy = () => {
  return (
    <Box mb={7}>
      <Section>
        <Box contain>
          <Text>
            This Messaging Program Privacy Policy explains how Jewlr collects
            and uses information about you in relation to its text message
            marketing program (the “Messaging Service”). We use Attentive to
            provide the Messaging Service to you. For the purposes of the
            Messaging Service, Attentive acts as our service provider and data
            processor of your information.
          </Text>
        </Box>
      </Section>
      <Section>
        <Box contain>
          <Text className="text-16 tablet:text-18" subtitle>
            Collection of Information
          </Text>
          <Text>
            We collect various information on our behalf from and about you,
            including information you directly provide when you use the
            Messaging Service. For example, we collect the phone number and
            email address you provided when signing up for the Messaging
            Service. When you send messages via the Messaging Service, we will
            also collect your messaging history and any information included in
            those messages.
          </Text>
          <Text>
            We may also collect information about you using cookies or similar
            technologies. Cookies are pieces of information that are stored by
            your browser on the hard drive or memory of your device. Cookies
            enable {jewlr_t("terms.personalization")} of your experience on the
            Messaging Service (e.g., sending you {jewlr_t("terms.personalized")}{" "}
            text messages such as shopping cart reminders).
          </Text>
          <Text>
            If you participate in a contest, sweepstakes, research study, or
            email survey associated with the Messaging Service, we will collect
            basic contact information and any other information you choose to
            provide in connection with these activities. We will also collect
            your contact information if you contact us with questions about the
            Messaging Service or for customer service.
          </Text>
        </Box>
      </Section>
      <Section>
        <Box contain>
          <Text className="text-16 tablet:text-18" subtitle>
            Consent for Data Collection and Messaging
          </Text>
          <Text>
            By signing up for our Messaging Service, you provide express consent
            to receive marketing messages from Jewlr. We maintain records of
            your consent, including the date and method of opt-in. Consent to
            receive automated marketing text messages is not a condition of any
            purchase.
          </Text>
        </Box>
      </Section>
      <Section>
        <Box contain>
          <Text className="text-16 tablet:text-18" subtitle>
            Use of Information
          </Text>
          <Text>
            We use your information to deliver, analyze, maintain, and support
            the Messaging Service. We may also use your information to enhance
            Messaging Service features and {jewlr_t("terms.customize")} and{" "}
            {jewlr_t("terms.personalize")} your experiences on the Messaging
            Service.
          </Text>
        </Box>
      </Section>
      <Section>
        <Box contain>
          <Text className="text-16 tablet:text-18" subtitle>
            Sharing of Information
          </Text>
          <Text>
            We may share, transfer, or disclose your information if you consent
            to us doing so, as well as in the following circumstances:
            <ul>
              <li>
                <b>Service Providers.</b> We may share your information with
                third parties to help us provide the Messaging Service to you.
              </li>
              <li>
                <b>Legal Requirement and Protection</b>. We may disclose your
                information as we believe such disclosure is necessary or
                appropriate to: (i) comply with applicable law and legal
                processes; (ii) respond to requests from public and government
                authorities, including public and government authorities outside
                your country of residence; (iii) enforce a contract with us;
                (iv) protect our rights, privacy, safety, or property, and/or
                that of our affiliates, you, or others; and (v) allow us to
                pursue available remedies or limit the damages that we may
                sustain.
              </li>
              <li>
                <b>Aggregate or De-identified Information</b>. From time to
                time, we may share aggregate or de-identified information about
                the use of the Messaging Service, which may be shared with third
                parties, including advertisers, promotional partners, and
                sponsors.
              </li>
            </ul>
          </Text>
          <br />
          <Text>
            <b>Cross-Border Data Transfers</b>
            <br />
            Your personal data may be stored and processed outside Canada,
            including in the United States, and may be subject to the laws of
            those jurisdictions. By using the Messaging Service, you consent to
            the transfer of your personal data outside Canada.
          </Text>
          <Text>
            <b>Exclusion of Opt-In Data Sharing</b>
            <br />
            All the above categories exclude text messaging originator opt-in
            data and consent; this information will not be shared with any third
            parties.
          </Text>
        </Box>
      </Section>
      <Section>
        <Box contain>
          <Text className="text-16 tablet:text-18" subtitle>
            Protection of Information
          </Text>
          <Text>
            We take a variety of physical, technical, administrative, and
            organizational security measures based on the sensitivity of the
            information we collect to protect your information against
            accidental or unlawful destruction or accidental loss, alteration,
            unauthorized disclosure, or access.
          </Text>
          <Text>
            Unfortunately, no online activity can be guaranteed to be 100%
            secure. While we strive to protect your information against
            unauthorized use or disclosure, we cannot ensure or warrant the
            security of any information you provide. We do not accept liability
            for unintentional disclosure.
          </Text>
        </Box>
      </Section>
      <Section>
        <Box contain>
          <Text className="text-16 tablet:text-18" subtitle>
            Retention of Information
          </Text>
          <Text>
            We retain your information for as long as you participate in the
            Messaging Service or as required to comply with applicable legal
            obligations. Specifically, we retain data for up to{" "}
            <b>12 months after opt-out</b> unless a longer period is required by
            law. We also retain and use your information as necessary to resolve
            disputes, protect our rights, and enforce our agreements.
          </Text>
        </Box>
      </Section>
      <Section>
        <Box contain>
          <Text className="text-16 tablet:text-18" subtitle>
            Your Rights and Choices
          </Text>
          <Text>
            Under <b>PIPEDA</b>, you have the right to:
            <ul>
              <li>Access the personal data we hold about you.</li>
              <li>Request corrections to inaccurate or incomplete data.</li>
              <li>Withdraw consent and request deletion of your data.</li>
            </ul>
            To exercise these rights, please contact us at{" "}
            <a
              href={`mailto:${sl.config.customer_service_email}`}
              rel="nofollow"
            >
              {sl.config.customer_service_email}
            </a>
            .
          </Text>
          <Text>
            <b>Opt-Out Process</b>
            <br />
            You can opt-out of receiving further commercial text messages via
            the Messaging Service by responding to any of our text messages with
            any of the following replies: STOP, END, CANCEL, UNSUBSCRIBE, or
            QUIT. You may also opt-out by emailing{" "}
            <a
              href={`mailto:${sl.config.customer_service_email}`}
              rel="nofollow"
            >
              {sl.config.customer_service_email}
            </a>{" "}
            or adjusting your preferences in your account settings.
          </Text>
        </Box>
      </Section>
      <Section>
        <Box contain>
          <Text className="text-16 tablet:text-18" subtitle>
            Customer Care
          </Text>
          <Text>
            If you are experiencing any problems with the Messaging Service,
            please visit <Link to="/helpdesk">Jewlr Helpdesk</Link> and submit
            the details about your problem or your request for support, or email{" "}
            <a
              href={`mailto:${sl.config.customer_service_email}`}
              rel="nofollow"
            >
              {sl.config.customer_service_email}
            </a>
            .
          </Text>
        </Box>
      </Section>
      <Section>
        <Box contain>
          <Text className="text-16 tablet:text-18" subtitle>
            Main Privacy Policy
          </Text>
          <Text>
            By signing up to receive text messages from us, you also agree to
            our main <Link to="/info/privacy-policy">Privacy Policy</Link>.
          </Text>
        </Box>
      </Section>
    </Box>
  )
}

export default SMSPrivacyPolicy
